import React, { useState, useEffect, useRef } from "react";
import Header from "../../components/Header";
import { Link, Navigate } from "react-router-dom";
import { useCurrentUser } from "../../utils";
import { useDarkMode } from "../../App";
import Loading from "../../components/Loading";
import { useQuery, useMutation } from '@apollo/client';
import getUserByIdQuery from '../../graphql/query/getUserById.query';
import getSellerVerificationsQuery from '../../graphql/query/getSellerVerifications.query';
import getSellerReviewsWithBuyersQuery from '../../graphql/query/getSellerReviewsWithBuyers.query';
import updateUserMutation from '../../graphql/mutations/updateUser.mutation';
import { useUserListings } from "../../services";
import { formatImageURL } from "../../utils";
import { uploadFile as uploadToStorage } from "../../services/uploads";
import "./ProfilePage.css";
import {
  compressImage,
  validateImageFileAsync,
  MIN_PROFILE_IMAGE_SHORT_EDGE,
} from "../../utils/imageCompression";
import { AUTH_API_URL, authFetch } from "../../lib/authFetch";

type TabType = 'profile' | 'statistics' | 'settings';

const ProfilePage = () => {
  const currentUser = useCurrentUser();
  const userId = currentUser?.id;
  const { darkMode } = useDarkMode();
  const [activeTab, setActiveTab] = useState<TabType>('profile');
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Edit mode states
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [editForm, setEditForm] = useState({
    firstname: '',
    lastname: '',
    email: '',
  });

  // Profile picture states
  const [profilePictureFile, setProfilePictureFile] = useState<File | null>(null);
  const [profilePicturePreview, setProfilePicturePreview] = useState<string | null>(null);
  const [isUploadingPicture, setIsUploadingPicture] = useState(false);

  // Verification states
  const [emailVerificationToken, setEmailVerificationToken] = useState('');

  // Password change states
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');

  // General message states
  const [profileMessage, setProfileMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

  // Fetch user data
  const { data: userData, loading: userLoading, refetch: refetchUser } = useQuery(getUserByIdQuery, {
    variables: { userId: userId as number },
    skip: !userId,
  });

  // Fetch verifications
  const { loading: verificationsLoading, refetch: refetchVerifications } = useQuery(
    getSellerVerificationsQuery,
    {
      variables: { userId: userId as number },
      skip: !userId,
    }
  );

  // Fetch reviews (with reviewer names — requires auth)
  const { data: reviewsData } = useQuery(getSellerReviewsWithBuyersQuery, {
    variables: { sellerId: userId as number },
    skip: !userId,
  });

  // Fetch listing stats
  const { data: activeData } = useUserListings({
    variables: { sellerId: userId as number, active: true },
    skip: !userId,
  });
  const { data: inactiveData } = useUserListings({
    variables: { sellerId: userId as number, active: false },
    skip: !userId,
  });

  const [updateUser] = useMutation(updateUserMutation);

  useEffect(() => {
    if (userData?.userById) {
      const userDataById = userData.userById;
      setEditForm({
        firstname: userDataById.firstname || '',
        lastname: userDataById.lastname || '',
        email: userDataById.email || '',
      });
      // Reset preview to existing profile picture if no new file selected
      if (!profilePictureFile && userDataById.profilePictureUrl) {
        setProfilePicturePreview(formatImageURL(userDataById.profilePictureUrl));
      } else if (!profilePictureFile) {
        setProfilePicturePreview(null);
      }
    }
  }, [userData, profilePictureFile]);

  // Upload profile picture via server-issued presigned URL (no AWS creds here).
  const uploadProfilePicture = async (file: File): Promise<string> => {
    return uploadToStorage(file, "profile");
  };

  const handleProfilePictureSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    const input = e.target;
    if (!file) {
      if (input) input.value = '';
      return;
    }

    void (async () => {
      const validation = await validateImageFileAsync(file, {
        minShortEdge: MIN_PROFILE_IMAGE_SHORT_EDGE,
      });
      if (!validation.valid) {
        alert(validation.error || 'Invalid image file');
        if (input) input.value = '';
        return;
      }
      setProfilePictureFile(file);
      setProfilePicturePreview(URL.createObjectURL(file));
      if (input) input.value = '';
    })();
  };

  const handleUploadProfilePicture = async () => {
    if (!profilePictureFile) {
      return;
    }

    setIsUploadingPicture(true);
    try {
      // Compress image before upload
      const compressedFile = await compressImage(profilePictureFile);
      const fileKey = await uploadProfilePicture(compressedFile);
      // Store just the key, formatImageURL will handle the full URL construction
      
      // Update user with profile picture URL (store the key)
      await updateUser({
        variables: {
          id: userId,
          profilePictureUrl: fileKey
        }
      });
      
      setProfileMessage({ type: 'success', text: 'Profile picture uploaded successfully!' });
      setProfilePictureFile(null);
      refetchUser();
      setTimeout(() => setProfileMessage(null), 3000);
    } catch (error) {
      console.error('Error uploading profile picture:', error);
      setProfileMessage({ type: 'error', text: 'Failed to upload profile picture. Please try again.' });
      setTimeout(() => setProfileMessage(null), 5000);
    } finally {
      setIsUploadingPicture(false);
    }
  };

  if (!userId) {
    return <Navigate to="/login" />;
  }

  if (userLoading || verificationsLoading) {
    return <Loading />;
  }

  const userProfile = userData?.userById;
  const reviews = reviewsData?.allSellerReviews?.nodes || [];
  const activeListings = activeData?.allCarsForSales?.nodes || [];
  const inactiveListings = inactiveData?.allCarsForSales?.nodes || [];
  const totalListings = activeListings.length + inactiveListings.length;

  // Calculate verification level
  const getVerificationLevel = () => {
    if (userProfile?.identityVerified && userProfile?.averageRating && userProfile?.totalSales >= 3 && userProfile?.averageRating >= 4.0) {
      return 3; // Trusted Seller
    } else if (userProfile?.identityVerified) {
      return 2; // Identity Verified
    } else if (userProfile?.emailVerified) {
      return 1; // Verified Contact
    }
    return 0; // Unverified
  };

  const verificationLevel = getVerificationLevel();

  const handleUpdateProfile = async () => {
    // Basic validation
    if (!editForm.email || !editForm.email.includes('@')) {
      setProfileMessage({ type: 'error', text: 'Please enter a valid email address' });
      return;
    }

    try {
      // Preserve existing profile picture URL if no new file is being uploaded
      let profilePictureUrl = userProfile?.profilePictureUrl || null;
      
      // If a new profile picture was selected, upload it first
      if (profilePictureFile) {
        setIsUploadingPicture(true);
        try {
          const fileKey = await uploadProfilePicture(profilePictureFile);
          // Store just the key, formatImageURL will handle the full URL construction
          profilePictureUrl = fileKey;
          setProfilePictureFile(null);
        } catch (uploadError) {
          console.error('Error uploading profile picture:', uploadError);
          setProfileMessage({ type: 'error', text: 'Failed to upload profile picture. Please try again.' });
          setIsUploadingPicture(false);
          return;
        } finally {
          setIsUploadingPicture(false);
        }
      }

      await updateUser({
        variables: {
          id: userId,
          firstname: editForm.firstname,
          lastname: editForm.lastname,
          email: editForm.email,
          profilePictureUrl: profilePictureUrl
        }
      });
      setIsEditingProfile(false);
      setProfileMessage({ type: 'success', text: 'Profile updated successfully!' });
      refetchUser();
      setTimeout(() => setProfileMessage(null), 3000);
    } catch (error) {
      console.error('Error updating profile:', error);
      setProfileMessage({ type: 'error', text: 'Failed to update profile. Please try again.' });
      setTimeout(() => setProfileMessage(null), 5000);
    }
  };

  const handleRequestEmailVerification = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await authFetch(`${AUTH_API_URL}/verify-email/request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ email: userProfile?.email })
      });
      const data = await response.json();
      if (response.ok) {
        setEmailVerificationToken(data.verification_token);
        alert(`Verification token: ${data.verification_token}\n(In production, this would be sent via email)`);
      } else {
        alert('Failed to request email verification');
      }
    } catch (error) {
      console.error('Error requesting email verification:', error);
      alert('Failed to request email verification');
    }
  };

  const handleConfirmEmailVerification = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await authFetch(`${AUTH_API_URL}/verify-email/confirm`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ token: emailVerificationToken })
      });
      if (response.ok) {
        alert('Email verified successfully!');
        setEmailVerificationToken('');
        refetchUser();
        refetchVerifications();
      } else {
        const data = await response.json();
        alert(data.detail || 'Failed to verify email');
      }
    } catch (error) {
      console.error('Error confirming email verification:', error);
      alert('Failed to verify email');
    }
  };

  const handleChangePassword = async () => {
    // Validation
    if (passwordForm.newPassword.length < 6) {
      setPasswordError('Password must be at least 6 characters long');
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }

    if (passwordForm.currentPassword === passwordForm.newPassword) {
      setPasswordError('New password must be different from current password');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await authFetch(`${AUTH_API_URL}/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          current_password: passwordForm.currentPassword,
          new_password: passwordForm.newPassword
        })
      });

      const data = await response.json();
      
      if (response.ok) {
        setPasswordSuccess('Password changed successfully!');
        setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
        setPasswordError('');
        setTimeout(() => setPasswordSuccess(''), 5000);
      } else {
        setPasswordError(data.detail || 'Failed to change password');
        setPasswordSuccess('');
      }
    } catch (error) {
      console.error('Error changing password:', error);
      setPasswordError('Failed to change password. Please try again.');
      setPasswordSuccess('');
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className={`profile-page-container ${darkMode ? 'dark' : ''}`}>
      <Header />
      <div className="profile-header">
        <h1 className="profile-title">Profile</h1>
      </div>

      {/* Tab Navigation */}
      <div className="profile-tabs">
        <button
          className={`profile-tab ${activeTab === 'profile' ? 'active' : ''}`}
          onClick={() => setActiveTab('profile')}
        >
          Profile Info
        </button>
        <button
          className={`profile-tab ${activeTab === 'statistics' ? 'active' : ''}`}
          onClick={() => setActiveTab('statistics')}
        >
          Statistics
        </button>
        <button
          className={`profile-tab ${activeTab === 'settings' ? 'active' : ''}`}
          onClick={() => setActiveTab('settings')}
        >
          Account Settings
        </button>
      </div>

      {/* Success/Error Messages */}
      {profileMessage && (
        <div className={`profile-message ${profileMessage.type}`}>
          {profileMessage.text}
        </div>
      )}

      {/* Tab Content */}
      <div className="profile-content">
        {activeTab === 'profile' && (
          <div className="profile-info-tab">
            {/* Profile Picture Section */}
            <div className="profile-section profile-picture-section">
              <div className="profile-section-header">
                <h2>Profile Picture</h2>
              </div>
              <div className="profile-picture-container">
                <div className="profile-picture-display">
                  {profilePicturePreview || userProfile?.profilePictureUrl ? (
                    <img 
                      src={profilePicturePreview || formatImageURL(userProfile?.profilePictureUrl) || '/images/logo_light.jpg'} 
                      alt="Profile" 
                      className="profile-picture-image"
                    />
                  ) : (
                    <div className="profile-picture-placeholder">
                      <span className="placeholder-icon">👤</span>
                      <span className="placeholder-text">No Photo</span>
                    </div>
                  )}
                </div>
                <div className="profile-picture-actions">
                  {!userProfile?.profilePictureUrl && !profilePicturePreview && (
                    <div className="profile-picture-encouragement">
                      <p className="encouragement-text">
                        <strong>Build Trust with Buyers & Sellers!</strong>
                      </p>
                      <p className="encouragement-subtext">
                        Upload a clear photo of yourself to help others feel more confident when doing business with you.
                      </p>
                    </div>
                  )}
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleProfilePictureSelect}
                    accept="image/*"
                    style={{ display: 'none' }}
                  />
                  {profilePicturePreview && !isUploadingPicture && (
                    <div className="profile-picture-preview-actions">
                      <button
                        className="upload-picture-button"
                        onClick={handleUploadProfilePicture}
                      >
                        Upload Photo
                      </button>
                      <button
                        className="cancel-picture-button"
                        onClick={() => {
                          setProfilePictureFile(null);
                          setProfilePicturePreview(userProfile?.profilePictureUrl ? formatImageURL(userProfile.profilePictureUrl) : null);
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                  {!profilePicturePreview && (
                    <button
                      className="select-picture-button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={isUploadingPicture}
                    >
                      {userProfile?.profilePictureUrl ? 'Change Photo' : 'Upload Photo'}
                    </button>
                  )}
                  {isUploadingPicture && (
                    <div className="uploading-indicator">Uploading...</div>
                  )}
                </div>
              </div>
            </div>

            <div className="profile-section">
              <div className="profile-section-header">
                <h2>Personal Information</h2>
                {!isEditingProfile && (
                  <button className="edit-button" onClick={() => setIsEditingProfile(true)}>
                    Edit
                  </button>
                )}
              </div>

              {isEditingProfile ? (
                <div className="profile-edit-form">
                  <div className="form-group">
                    <label>First Name</label>
                    <input
                      type="text"
                      value={editForm.firstname}
                      onChange={(e) => setEditForm({ ...editForm, firstname: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label>Last Name</label>
                    <input
                      type="text"
                      value={editForm.lastname}
                      onChange={(e) => setEditForm({ ...editForm, lastname: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label>Email</label>
                    <input
                      type="email"
                      value={editForm.email}
                      onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label>Profile Picture</label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleProfilePictureSelect}
                      style={{ marginTop: '8px' }}
                    />
                    {profilePicturePreview && (
                      <div className="profile-picture-edit-preview">
                        <img src={profilePicturePreview} alt="Preview" style={{ width: '100px', height: '100px', objectFit: 'cover', borderRadius: '50%', marginTop: '8px' }} />
                      </div>
                    )}
                    <small className="form-hint">Upload a clear photo of yourself to build trust (max 5MB)</small>
                  </div>
                  <div className="form-actions">
                    <button className="save-button" onClick={handleUpdateProfile}>
                      Save
                    </button>
                    <button className="cancel-button" onClick={() => setIsEditingProfile(false)}>
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="profile-info-display">
                  <div className="info-item">
                    <span className="info-label">Email:</span>
                    <span className="info-value">{userProfile?.email || 'N/A'}</span>
                    {userProfile?.emailVerified ? (
                      <span className="verified-badge verified">✓ Verified</span>
                    ) : (
                      <span className="verified-badge unverified">Not Verified</span>
                    )}
                  </div>
                  <div className="info-item">
                    <span className="info-label">First Name:</span>
                    <span className="info-value">{userProfile?.firstname || 'N/A'}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Last Name:</span>
                    <span className="info-value">{userProfile?.lastname || 'N/A'}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Member Since:</span>
                    <span className="info-value">{formatDate(userProfile?.createdAt)}</span>
                  </div>
                </div>
              )}
            </div>

            <div className="profile-section">
              <div className="profile-section-header">
                <h2>Verification Status</h2>
              </div>
              <div className="verification-status">
                <div className="verification-level">
                  <span className="level-label">Verification Level:</span>
                  <span className={`level-badge level-${verificationLevel}`}>
                    {verificationLevel === 3 ? 'Trusted Seller' :
                     verificationLevel === 2 ? 'Identity Verified' :
                     verificationLevel === 1 ? 'Verified Contact' : 'Unverified'}
                  </span>
                </div>

                <div className="verification-actions">
                  {!userProfile?.emailVerified && (
                    <div className="verification-item">
                      <div className="verification-info">
                        <span>Email Verification</span>
                        <span className="status-badge pending">Pending</span>
                      </div>
                      <div className="verification-controls">
                        <button className="verify-button" onClick={handleRequestEmailVerification}>
                          Request Verification
                        </button>
                        {emailVerificationToken && (
                          <div className="verification-input-group">
                            <input
                              type="text"
                              placeholder="Enter verification token"
                              value={emailVerificationToken}
                              onChange={(e) => setEmailVerificationToken(e.target.value)}
                            />
                            <button className="confirm-button" onClick={handleConfirmEmailVerification}>
                              Confirm
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {!userProfile?.identityVerified && (
                    <div className="verification-item">
                      <div className="verification-info">
                        <span>Identity Verification</span>
                        <span className="status-badge pending">Pending</span>
                      </div>
                      <div className="verification-note">
                        <p>Upload a government-issued ID (driver's license or state ID) for manual review.</p>
                        <p className="note-text">This feature will be available after identity upload functionality is implemented.</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'statistics' && (
          <div className="statistics-tab">
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-value">{totalListings}</div>
                <div className="stat-label">Total Listings</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">{activeListings.length}</div>
                <div className="stat-label">Active Listings</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">{inactiveListings.length}</div>
                <div className="stat-label">Inactive Listings</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">{userProfile?.totalSales || 0}</div>
                <div className="stat-label">Completed Sales</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">{userProfile?.averageRating ? userProfile.averageRating.toFixed(1) : 'N/A'}</div>
                <div className="stat-label">Average Rating</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">{userProfile?.totalReviews || 0}</div>
                <div className="stat-label">Total Reviews</div>
              </div>
            </div>

            <div className="quick-links">
              <Link to="/mylistings" className="quick-link-button">
                View My Listings
              </Link>
              <Link to="/newlisting" className="quick-link-button">
                Create New Listing
              </Link>
            </div>

            {reviews.length > 0 && (
              <div className="reviews-section">
                <h3>Recent Reviews</h3>
                <div className="reviews-list">
                  {reviews.slice(0, 5).map((review: any) => (
                    <div key={review.id} className="review-item">
                      <div className="review-header">
                        <span className="reviewer-name">
                          {review.buyer?.firstname} {review.buyer?.lastname}
                        </span>
                        <span className="review-rating">⭐ {review.rating}/5</span>
                      </div>
                      {review.reviewText && (
                        <p className="review-text">{review.reviewText}</p>
                      )}
                      <div className="review-meta">
                        {review.listing && (
                          <span className="review-listing">
                            {review.listing.year} {review.listing.make} {review.listing.model}
                          </span>
                        )}
                        <span className="review-date">{formatDate(review.createdAt)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'settings' && (
          <div className="settings-tab">
            <div className="settings-section">
              <h2>Change Password</h2>
              <form 
                className="password-change-form"
                onSubmit={(e) => {
                  e.preventDefault();
                  handleChangePassword();
                }}
              >
                <div className="form-group">
                  <label>Current Password</label>
                  <input
                    type="password"
                    value={passwordForm.currentPassword}
                    onChange={(e) => {
                      setPasswordForm({ ...passwordForm, currentPassword: e.target.value });
                      setPasswordError('');
                    }}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>New Password</label>
                  <input
                    type="password"
                    value={passwordForm.newPassword}
                    onChange={(e) => {
                      setPasswordForm({ ...passwordForm, newPassword: e.target.value });
                      setPasswordError('');
                    }}
                    required
                    minLength={6}
                  />
                  <small className="form-hint">Password must be at least 6 characters long</small>
                </div>
                <div className="form-group">
                  <label>Confirm New Password</label>
                  <input
                    type="password"
                    value={passwordForm.confirmPassword}
                    onChange={(e) => {
                      setPasswordForm({ ...passwordForm, confirmPassword: e.target.value });
                      setPasswordError('');
                    }}
                    required
                  />
                </div>
                {passwordError && (
                  <div className="form-error">{passwordError}</div>
                )}
                {passwordSuccess && (
                  <div className="form-success">{passwordSuccess}</div>
                )}
                <div className="form-actions">
                  <button type="submit" className="save-button">
                    Change Password
                  </button>
                  <button 
                    type="button" 
                    className="cancel-button"
                    onClick={() => {
                      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
                      setPasswordError('');
                      setPasswordSuccess('');
                    }}
                  >
                    Clear
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfilePage;
